<script setup lang='ts'>
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  PinInput,
  PinInputGroup,
  PinInputSlot,
} from "@/components/ui/pin-input"
import { ref, computed } from "vue"
import { UserApi } from '@js-app/office/api/user-api'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from "vuex";

const value = ref<string[]>([])
const hasError = ref(false)
const error = ref<string | null>(null)
const attemptsLeft = ref<number | null>(null)
const router = useRouter()
const route = useRoute()
const store = useStore();
const LOGIN_USER = (payload: any) => store.dispatch('LOGIN_USER', payload);

const phoneMask = computed(() => {
  if (window.history.state && window.history.state.phone_mask) {
    return window.history.state.phone_mask;
  }

  return '';
});

async function handleComplete(code: string[]) {
  try {
    const otpCode = code.join("");
    const _authResponse = await UserApi.sendOTPCode(otpCode);

    const payload = {
      accessToken: _authResponse.data.access_token,
      refreshToken: _authResponse.data.refresh_token,
    }

    await LOGIN_USER(payload);

    await router.push({ name: _authResponse.data.redirect });

  } catch (e) {
    const responseData = e.data;
    hasError.value = true;

    if (responseData) {
      error.value = responseData.message;
      attemptsLeft.value = responseData.attempts_left;

      if (responseData.redirect_to_error) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        await router.push({name: 'login-otp-error'});
      }
    }
  }
}
</script>

<template>
  <Card class="w-[400px]">
    <CardHeader>
      <CardTitle class="flex flex-col items-center gap-y-2 text-center">
        {{ $t('cabinet-two-factor.title') }}
      </CardTitle>
      <CardDescription class="flex justify-center text-center">
        {{ $t('cabinet-two-factor.description', { phoneMask: phoneMask }) }}
      </CardDescription>
    </CardHeader>

    <CardContent>
      <div v-if="error" class="flex justify-center text-sm text-red-600 mb-4">
        <div v-if="attemptsLeft !== null && attemptsLeft > 1" class="flex justify-center text-sm text-red mb-4">
          {{ $t('auth.otp.attempts_left', {count: attemptsLeft}) }}
        </div>
        <div v-else-if="attemptsLeft === 1" class="flex justify-center text-sm text-red mb-4">
          {{ $t('auth.otp.attempts_left_one', {count: attemptsLeft}) }}
        </div>
        <div v-else class="flex justify-center text-sm text-red mb-4">
          {{ $t('auth.otp.attempts_used') }}
        </div>
      </div>

      <form class="flex flex-col gap-y-6">
        <div class="flex justify-center">
          <PinInput
              id="pin-input"
              v-model="value"
              placeholder="-"
          >
            <PinInputGroup>
              <PinInputSlot
                  v-for="(id, index) in 4"
                  :key="id"
                  :index="index"
                  class="w-12 h-12 text-lg border-gray-300"
                  :class="{
                'border-red-500': hasError,
                'border-gray-300': !hasError
                }"
              />
            </PinInputGroup>
          </PinInput>
        </div>
      </form>
    </CardContent>

    <CardFooter class="flex justify-between px-6">
      <Button class="w-full bg-[#64748B] transition-colors hover:bg-[#64748B] hover:opacity-75" @click="handleComplete(value)">
        {{ $t('cabinet-two-factor.continue_button') }}
      </Button>
    </CardFooter>
  </Card>
</template>
